import { useState } from 'react';
import type { EobCheck, PostingItem } from '../types';
import { amountAria, money, signedMoney } from '../utils';
import { isPlainClick } from '@/components/plainClick';
import { Link } from 'react-router-dom';
import { PMS } from '@/features/shared/pms';

function CheckRow({ check, selected, onSelect }: { check: EobCheck; selected: boolean; onSelect: (id: string) => void }) {
  return (
    <a href={`/posting/${check.id}`} className={`po-qrow${selected ? ' po-qsel' : ''}`} aria-current={selected ? 'page' : undefined} onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); onSelect(check.id); }}>
      <div className="po-qtop">
        <span className="po-qname">{check.payer}</span>
        <span className="po-qamt num">{money(check.amount)}</span>
      </div>
      <div className="po-qsub">{check.listSecond}</div>
      <div className="po-qtagline">
        <span className={`po-badge ${check.group === 'needs_you' ? 'b-warn' : 'b-info'}`}>{check.group === 'needs_you' ? 'Needs you' : 'Ready'}</span>
        <span>{check.listReason}</span>
      </div>
      <div className="po-qmeta">{check.listMeta}</div>
    </a>
  );
}

/** The two cases that have their own badge in the list (D197, D198). */
function caseBadge(item: PostingItem): { label: string; cls: string } | null {
  if (item.kind === 'paid_to_patient') return { label: 'Paid to the patient', cls: 'b-mute' };
  if (item.kind === 'patient_credit') return { label: 'Patient credit', cls: 'b-info' };
  return null;
}

function Row({ item, selected, onSelect }: { item: PostingItem; selected: boolean; onSelect: (id: string) => void }) {
  const needsYou = item.group === 'needs_you';
  const cb = caseBadge(item);
  return (
    <a
      href={`/posting/${item.id}`}
      className={`po-qrow${selected ? ' po-qsel' : ''}`}
      aria-current={selected ? 'page' : undefined}
      onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); onSelect(item.id); }}
    >
      <div className="po-qtop">
        <span className="po-qname">{item.patient}</span>
        <span className="po-qamt num" aria-label={amountAria(item.amount)}>{signedMoney(item.amount)}</span>
      </div>
      <div className="po-qsub">{item.payer.name}</div>
      {cb ? (
        <div className="po-qtagline">
          <span className={`po-badge ${cb.cls}`}>{cb.label}</span>
          <span className="po-meta">{item.listMeta}</span>
        </div>
      ) : (
        <>
          <div className="po-qtagline">
            {item.group === 'checking' ? (
              <span className="po-badge b-warn">Checking with {PMS}</span>
            ) : (
              <>
                <span className={`po-badge ${needsYou ? 'b-warn' : 'b-info'}`}>{needsYou ? 'Needs you' : 'Ready'}</span>
                <span>{item.reason}</span>
              </>
            )}
          </div>
          <div className="po-qmeta">{item.listMeta}</div>
        </>
      )}
    </a>
  );
}

export function ToPostList({
  needsYou,
  ready,
  checking = [],
  needsYouChecks = [],
  readyChecks = [],
  selectedId,
  deniedCount,
  onSelect,
}: {
  needsYou: PostingItem[];
  ready: PostingItem[];
  /** D238: postings ELVA is checking in the PMS again. Nothing for a person to do. */
  checking?: PostingItem[];
  needsYouChecks?: EobCheck[];
  readyChecks?: EobCheck[];
  selectedId?: string;
  deniedCount: number;
  onSelect: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const match = (it: PostingItem): boolean => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const claimNo = it.compare?.find((c) => c.label === 'Claim number')?.claim ?? '';
    return (
      it.patient.toLowerCase().includes(needle) ||
      it.payer.name.toLowerCase().includes(needle) ||
      money(it.amount).toLowerCase().includes(needle) ||
      it.trace.toLowerCase().includes(needle) ||
      (it.payment.checkNo ?? '').includes(needle) ||
      claimNo.toLowerCase().includes(needle)
    );
  };
  const ny = needsYou.filter(match);
  const rd = ready.filter(match);
  const ck = checking.filter(match);
  const nyTotal = ny.length + needsYouChecks.length;
  const rdTotal = rd.length + readyChecks.length;

  return (
    <div className="po-queue">
      <div className="po-search-wrap">
        <input className="po-search" type="search" placeholder="Search patient, payer, amount, trace or claim" aria-label="Search patient, payer, amount, trace or claim" value={q} onChange={(e) => { setQ(e.target.value); }} />
      </div>
      {nyTotal > 0 ? (
        <>
          <p className="po-group-head">Needs you · <span className="po-count">{nyTotal}</span></p>
          {needsYouChecks.map((c) => <CheckRow key={c.id} check={c} selected={c.id === selectedId} onSelect={onSelect} />)}
          {ny.map((it) => <Row key={it.id} item={it} selected={it.id === selectedId} onSelect={onSelect} />)}
        </>
      ) : null}
      {rdTotal > 0 ? (
        <>
          <p className="po-group-head">Ready to post · <span className="po-count">{rdTotal}</span></p>
          {readyChecks.map((c) => <CheckRow key={c.id} check={c} selected={c.id === selectedId} onSelect={onSelect} />)}
          {rd.map((it) => <Row key={it.id} item={it} selected={it.id === selectedId} onSelect={onSelect} />)}
        </>
      ) : null}
      {ck.length > 0 ? (
        <>
          <p className="po-group-head">ELVA is checking · <span className="po-count">{ck.length}</span></p>
          {ck.map((it) => <Row key={it.id} item={it} selected={it.id === selectedId} onSelect={onSelect} />)}
        </>
      ) : null}
      <p className="po-meta" style={{ padding: '16px' }}>
        Posted, but paid $0 or less than your contract:{' '}
        <Link to="/denied" className="po-src" style={{ textDecoration: 'underline' }}>{deniedCount} claims on Denied &amp; underpaid</Link>
      </p>
    </div>
  );
}
